import { useState, useEffect, useCallback } from "react";
import { C } from "../data/constants";
import { API_BASE } from "../data/constants";

const ADMIN_PASSWORD = "Mu980207*"; // change this to your own password

const STATUS_COLORS = {
  new:       { bg: "#E3F2FD", color: "#1565C0", label: "New" },
  contacted: { bg: "#FFF8E1", color: "#F57F17", label: "Contacted" },
  booked:    { bg: "#E8F5E9", color: "#2E7D32", label: "Booked" },
  closed:    { bg: "#F5F5F5", color: "#757575", label: "Closed" },
};

function Badge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span style={{ background: s.bg, color: s.color, padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
      {s.label}
    </span>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: C.white, borderRadius: 14, padding: "20px 24px", flex: 1, minWidth: 120, boxShadow: "0 2px 12px rgba(44,40,38,0.07)" }}>
      <p style={{ fontSize: 11, color: C.slate, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>{label}</p>
      <p style={{ fontSize: 36, fontWeight: 300, color: color || C.dark, fontFamily: "'Cormorant Garamond',serif", lineHeight: 1 }}>{value}</p>
    </div>
  );
}

function EnquiryRow({ enquiry, onStatusChange, onDelete, onClick }) {
  const [updating, setUpdating] = useState(false);

  const changeStatus = async (e) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    setUpdating(true);
    try {
      await fetch(`${API_BASE}/enquiries/${enquiry.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      onStatusChange(enquiry.id, newStatus);
    } catch {}
    setUpdating(false);
  };

  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete enquiry from ${enquiry.name}?`)) return;
    await fetch(`${API_BASE}/enquiries/${enquiry.id}`, { method: "DELETE" });
    onDelete(enquiry.id);
  };

  return (
    <tr
      onClick={onClick}
      style={{ cursor: "pointer", borderBottom: `1px solid ${C.linen}`, transition: "background 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.background = C.cream}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <td style={{ padding: "14px 16px" }}>
        <p style={{ fontWeight: 600, color: C.dark, fontSize: 14, marginBottom: 2 }}>{enquiry.name}</p>
        <p style={{ color: C.slate, fontSize: 12 }}>{enquiry.email}</p>
        {enquiry.whatsapp && <p style={{ color: C.slate, fontSize: 12 }}>{enquiry.whatsapp}</p>}
      </td>
      <td style={{ padding: "14px 16px" }}>
        <p style={{ color: C.mid, fontSize: 13 }}>{enquiry.eventType}</p>
        {enquiry.packageTier && <p style={{ color: C.slate, fontSize: 12, marginTop: 2 }}>{enquiry.packageTier}</p>}
      </td>
      <td style={{ padding: "14px 16px" }}>
        <p style={{ color: C.mid, fontSize: 13 }}>{enquiry.eventDate || "—"}</p>
      </td>
      <td style={{ padding: "14px 16px" }}>
        <p style={{ color: C.blush, fontWeight: 600, fontSize: 13 }}>{enquiry.estimatedTotal || "—"}</p>
      </td>
      <td style={{ padding: "14px 16px" }} onClick={e => e.stopPropagation()}>
        <select
          value={enquiry.status}
          onChange={changeStatus}
          disabled={updating}
          style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${C.linen}`, fontSize: 12, background: C.white, color: C.dark, cursor: "pointer" }}
        >
          {Object.entries(STATUS_COLORS).map(([val, { label }]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </td>
      <td style={{ padding: "14px 16px" }}>
        <p style={{ color: C.slate, fontSize: 12 }}>{new Date(enquiry.createdAt).toLocaleDateString("en-ZA")}</p>
      </td>
      <td style={{ padding: "14px 16px" }} onClick={e => e.stopPropagation()}>
        <button onClick={del} style={{ background: "none", border: "none", color: "#e53935", cursor: "pointer", fontSize: 16, padding: "4px 8px" }} title="Delete">🗑</button>
      </td>
    </tr>
  );
}

function EnquiryModal({ enquiry, onClose, onStatusChange }) {
  if (!enquiry) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(44,40,38,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: C.white, borderRadius: 20, maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto", position: "relative" }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, background: C.blush, border: "none", borderRadius: "50%", width: 32, height: 32, fontSize: 16, color: C.white, cursor: "pointer" }}>×</button>

        <div style={{ padding: "28px 28px 24px", borderBottom: `1px solid ${C.linen}` }}>
          <p style={{ color: C.blush, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Enquiry Details</p>
          <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, color: C.dark, marginBottom: 8 }}>{enquiry.name}</h3>
          <Badge status={enquiry.status} />
        </div>

        <div style={{ padding: "24px 28px" }}>
          {[
            ["Email", enquiry.email],
            ["WhatsApp", enquiry.whatsapp || "Not provided"],
            ["Event Type", enquiry.eventType],
            ["Package Tier", enquiry.packageTier || "Not specified"],
            ["Event Date", enquiry.eventDate || "Not specified"],
            ["Estimated Total", enquiry.estimatedTotal || "N/A"],
            ["Source", enquiry.source || "website"],
            ["Submitted", new Date(enquiry.createdAt).toLocaleString("en-ZA")],
          ].map(([label, val]) => (
            <div key={label} style={{ display: "flex", gap: 16, padding: "8px 0", borderBottom: `1px solid ${C.cream}` }}>
              <span style={{ color: C.slate, fontSize: 12, width: 130, flexShrink: 0, textTransform: "uppercase", letterSpacing: 0.5, paddingTop: 1 }}>{label}</span>
              <span style={{ color: C.dark, fontSize: 14 }}>{val}</span>
            </div>
          ))}

          {enquiry.message && (
            <div style={{ marginTop: 16, background: C.cream, borderRadius: 10, padding: "14px 16px" }}>
              <p style={{ color: C.slate, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Message</p>
              <p style={{ color: C.mid, fontSize: 14, lineHeight: 1.7, fontStyle: "italic" }}>"{enquiry.message}"</p>
            </div>
          )}

          {enquiry.customItems && enquiry.customItems.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ color: C.slate, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Custom Build Items</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {enquiry.customItems.map((item, i) => (
                  <span key={i} style={{ background: C.linen, color: C.mid, padding: "4px 12px", borderRadius: 12, fontSize: 12 }}>
                    {item.name} — <strong>{item.price}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 24, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a href={`mailto:${enquiry.email}`} style={{ background: C.blush, color: C.white, padding: "10px 20px", borderRadius: 22, fontSize: 13, textDecoration: "none" }}>
              ✉️ Email Client
            </a>
            {enquiry.whatsapp && (
              <a href={`https://wa.me/${enquiry.whatsapp.replace(/\D/g,"")}`} target="_blank" rel="noreferrer"
                style={{ background: "#25D366", color: C.white, padding: "10px 20px", borderRadius: 22, fontSize: 13, textDecoration: "none" }}>
                💬 WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [pwErr, setPwErr] = useState(false);

  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("enquiries");

  const [testimonials, setTestimonials] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, sRes, tRes] = await Promise.all([
        fetch(`${API_BASE}/enquiries`),
        fetch(`${API_BASE}/enquiries/stats`),
        fetch(`${API_BASE}/testimonials/all`),
      ]);
      const e = await eRes.json();
      const s = await sRes.json();
      const t = await tRes.json();
      setEnquiries(e.data || []);
      setStats(s);
      setTestimonials(t.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const login = (e) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) { setAuthed(true); setPwErr(false); }
    else setPwErr(true);
  };

  const onStatusChange = (id, status) => {
    setEnquiries(prev => prev.map(e => e.id === id ? { ...e, status } : e));
    setStats(prev => ({ ...prev })); // trigger re-render
    load(); // refresh stats
  };

  const onDelete = (id) => {
    setEnquiries(prev => prev.filter(e => e.id !== id));
    if (selected?.id === id) setSelected(null);
    load();
  };

  const approveTestimonial = async (id) => {
    await fetch(`${API_BASE}/testimonials/${id}/approve`, { method: "PATCH" });
    setTestimonials(prev => prev.map(t => t.id === id ? { ...t, approved: true } : t));
  };

  const filtered = enquiries.filter(e => {
    const matchStatus = filter === "all" || e.status === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || e.name?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q) || e.eventType?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  // ── Login screen ──────────────────────────────────────────────
  if (!authed) {
    return (
      <div style={{ minHeight: "100vh", background: C.dark, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: C.white, borderRadius: 20, padding: "40px 36px", maxWidth: 380, width: "100%", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: C.dark, marginBottom: 6 }}>Admin Dashboard</h2>
          <p style={{ color: C.slate, fontSize: 13, marginBottom: 28 }}>Moments & Milestones</p>
          <form onSubmit={login} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <input
              type="password" placeholder="Enter admin password" value={pw}
              onChange={e => setPw(e.target.value)}
              style={{ padding: "12px 16px", border: `1px solid ${pwErr ? "#e53935" : C.linen}`, borderRadius: 10, fontSize: 14, color: C.dark }}
            />
            {pwErr && <p style={{ color: "#e53935", fontSize: 13 }}>Incorrect password</p>}
            <button type="submit" style={{ background: C.blush, color: C.white, border: "none", padding: "13px", borderRadius: 24, fontSize: 15, cursor: "pointer" }}>
              Sign In
            </button>
          </form>
          <p style={{ color: C.slate, fontSize: 11, marginTop: 20 }}>Default password: <strong>mm2025admin</strong><br />Change it in AdminDashboard.jsx</p>
        </div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#F5F3F0", fontFamily: "'Jost',sans-serif" }}>

      {/* Top bar */}
      <div style={{ background: C.dark, padding: "0 32px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: C.white, fontWeight: 400 }}>Moments & Milestones</h1>
          <span style={{ color: C.slate, fontSize: 12 }}>Admin</span>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button onClick={load} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: C.linen, padding: "6px 14px", borderRadius: 16, fontSize: 12, cursor: "pointer" }}>↻ Refresh</button>
          <button onClick={() => setAuthed(false)} style={{ background: "none", border: `1px solid rgba(255,255,255,0.2)`, color: C.slate, padding: "6px 14px", borderRadius: 16, fontSize: 12, cursor: "pointer" }}>Sign Out</button>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 24px" }}>

        {/* Stats */}
        <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
          <StatCard label="Total Enquiries" value={stats.total || 0} />
          <StatCard label="New" value={stats.new || 0} color="#1565C0" />
          <StatCard label="Contacted" value={stats.contacted || 0} color="#F57F17" />
          <StatCard label="Booked" value={stats.booked || 0} color="#2E7D32" />
          <StatCard label="Closed" value={stats.closed || 0} color={C.slate} />
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {[["enquiries","📋 Enquiries"], ["testimonials","⭐ Reviews"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding: "9px 20px", borderRadius: "10px 10px 0 0", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "'Jost',sans-serif",
                background: tab === key ? C.white : "transparent",
                color: tab === key ? C.dark : C.slate,
                fontWeight: tab === key ? 600 : 400,
                boxShadow: tab === key ? "0 -2px 8px rgba(44,40,38,0.06)" : "none",
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Enquiries Tab ── */}
        {tab === "enquiries" && (
          <div style={{ background: C.white, borderRadius: "0 16px 16px 16px", boxShadow: "0 2px 16px rgba(44,40,38,0.07)", overflow: "hidden" }}>

            {/* Filters */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.linen}`, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <input
                placeholder="Search name, email, event…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ padding: "8px 14px", border: `1px solid ${C.linen}`, borderRadius: 8, fontSize: 13, color: C.dark, minWidth: 220 }}
              />
              <div style={{ display: "flex", gap: 6 }}>
                {["all","new","contacted","booked","closed"].map(s => (
                  <button key={s} onClick={() => setFilter(s)}
                    style={{ padding: "6px 14px", borderRadius: 16, border: "none", cursor: "pointer", fontSize: 12, fontFamily: "'Jost',sans-serif",
                      background: filter === s ? C.blush : C.cream,
                      color: filter === s ? C.white : C.mid,
                    }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <p style={{ color: C.slate, fontSize: 12, marginLeft: "auto" }}>{filtered.length} enquir{filtered.length === 1 ? "y" : "ies"}</p>
            </div>

            {loading ? (
              <p style={{ padding: 40, textAlign: "center", color: C.slate, fontStyle: "italic" }}>Loading…</p>
            ) : filtered.length === 0 ? (
              <p style={{ padding: 40, textAlign: "center", color: C.slate, fontStyle: "italic" }}>No enquiries found.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: C.cream }}>
                      {["Client","Event","Date","Total","Status","Submitted",""].map(h => (
                        <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, color: C.slate, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(e => (
                      <EnquiryRow
                        key={e.id} enquiry={e}
                        onStatusChange={onStatusChange}
                        onDelete={onDelete}
                        onClick={() => setSelected(e)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Reviews Tab ── */}
        {tab === "testimonials" && (
          <div style={{ background: C.white, borderRadius: "0 16px 16px 16px", boxShadow: "0 2px 16px rgba(44,40,38,0.07)", padding: 24 }}>
            {testimonials.length === 0 ? (
              <p style={{ textAlign: "center", color: C.slate, fontStyle: "italic", padding: 40 }}>No reviews submitted yet.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {testimonials.map(t => (
                  <div key={t.id} style={{ border: `1px solid ${C.linen}`, borderRadius: 12, padding: "16px 18px", display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
                        <strong style={{ color: C.dark, fontSize: 14 }}>{t.name}</strong>
                        <span style={{ color: C.slate, fontSize: 12 }}>{t.event}</span>
                        {t.approved
                          ? <span style={{ background: "#E8F5E9", color: "#2E7D32", padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>✓ Approved</span>
                          : <span style={{ background: "#FFF8E1", color: "#F57F17", padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Pending</span>
                        }
                      </div>
                      <p style={{ color: C.mid, fontSize: 13, lineHeight: 1.7, fontStyle: "italic" }}>"{t.text}"</p>
                      <p style={{ color: C.slate, fontSize: 11, marginTop: 6 }}>{new Date(t.createdAt).toLocaleDateString("en-ZA")}</p>
                    </div>
                    {!t.approved && (
                      <button onClick={() => approveTestimonial(t.id)}
                        style={{ background: C.blush, color: C.white, border: "none", padding: "8px 18px", borderRadius: 18, fontSize: 13, cursor: "pointer", flexShrink: 0 }}>
                        Approve
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <EnquiryModal enquiry={selected} onClose={() => setSelected(null)} onStatusChange={onStatusChange} />
    </div>
  );
}
